/**
 * CRM 2.0 — US-003 RBAC Frontend
 *
 * RollenPage — Rollen & Rechte Verwaltung
 *
 * Sections:
 *  1. Rollenliste — alle Rollen mit Permission-Matrix zum An/Abwählen, Speichern
 *  2. Neue Rolle anlegen
 *  3. Admins — Rollenzuweisung pro Admin (basiert auf /v2/admin/rbac/* Endpoints)
 *
 * Auth: gated via <Can permission="admins.read"> — read-only for admins.read,
 *        write-actions (Speichern, Erstellen) require admins.write via the API.
 *
 * Patterns reused:
 *   - Card, Button, Badge, Drawer, Field, Input, Toggle, SectionTitle (ui/index.tsx)
 *   - getThemeColors, BRAND (lib/theme.ts)
 *   - usePermissions, usePermission (contexts/PermissionsContext.tsx)
 *   - Can (components/auth/Can.tsx)
 *   - apiV2 (lib/api.ts)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Drawer, Field, Input, SectionTitle, Toggle } from '../../components/ui';
import { Icon } from '../../components/icons';
import { Can } from '../../components/auth/Can';
import { usePermissions } from '../../contexts/PermissionsContext';
import { BRAND, getThemeColors } from '../../lib/theme';
import { apiV1, apiV2 } from '../../lib/api';
import type { Density } from '../../types';

// ── API types ──────────────────────────────────────────────────────────────

interface RoleData {
  id: number;
  name: string;
  permissions: string[];
}

interface PermissionData {
  key: string;
  label: string;
  group: string;
}

interface V1Admin {
  id: number;
  vorname?: string;
  nachname?: string;
  email?: string;
  role?: number | null;
}

// ── API helpers ────────────────────────────────────────────────────────────

const rbacApi = {
  getRoles: async (): Promise<RoleData[]> => {
    const res = await apiV2.get<{ data: RoleData[] }>('/admin/rbac/roles');
    return res.data.data;
  },
  getPermissions: async (): Promise<PermissionData[]> => {
    const res = await apiV2.get<{ data: PermissionData[] }>('/admin/rbac/permissions');
    return res.data.data;
  },
  createRole: async (name: string): Promise<RoleData> => {
    const res = await apiV2.post<{ data: RoleData }>('/admin/rbac/roles', { name });
    return res.data.data;
  },
  updateRole: async (id: number, name: string): Promise<RoleData> => {
    const res = await apiV2.put<{ data: RoleData }>(`/admin/rbac/roles/${id}`, { name });
    return res.data.data;
  },
  deleteRole: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/rbac/roles/${id}`);
  },
  syncPermissions: async (roleId: number, permissions: string[]): Promise<RoleData> => {
    const res = await apiV2.put<{ data: RoleData }>(`/admin/rbac/roles/${roleId}/permissions`, { permissions });
    return res.data.data;
  },
  assignRole: async (adminId: number, roleId: number | null): Promise<void> => {
    await apiV2.put(`/admin/rbac/admins/${adminId}/role`, { role_id: roleId });
  },
  getDriver: async (): Promise<string> => {
    const res = await apiV2.get<{ data: { driver: string } }>('/admin/rbac/driver');
    return res.data.data.driver;
  },
};

// ── Component ──────────────────────────────────────────────────────────────

interface Props {
  dark: boolean;
  density?: Density;
}

export function RollenPage({ dark }: Props) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const { hasPermission, refresh: refreshPermissions } = usePermissions();
  const canWrite = hasPermission('admins.write');

  const [roles, setRoles]             = useState<RoleData[]>([]);
  const [permissions, setPermissions] = useState<PermissionData[]>([]);
  const [admins, setAdmins]           = useState<V1Admin[]>([]);
  const [driver, setDriver]           = useState<string>('custom');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  // Matrix editing
  const [editingRole, setEditingRole]   = useState<RoleData | null>(null);
  const [matrixPerms, setMatrixPerms]   = useState<Set<string>>(new Set());
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [matrixError, setMatrixError]   = useState<string | null>(null);

  // Role drawer
  const [roleDrawerOpen, setRoleDrawerOpen] = useState(false);
  const [roleDrawerData, setRoleDrawerData] = useState<{ id?: number; name: string }>({ name: '' });
  const [roleSaving, setRoleSaving]         = useState(false);
  const [roleError, setRoleError]           = useState<string | null>(null);

  // Admin assignment drawer
  const [assignDrawerOpen, setAssignDrawerOpen]     = useState(false);
  const [assignAdmin, setAssignAdmin]               = useState<V1Admin | null>(null);
  const [assignRoleId, setAssignRoleId]             = useState<number | null>(null);
  const [assignSaving, setAssignSaving]             = useState(false);
  const [assignError, setAssignError]               = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, p] = await Promise.all([rbacApi.getRoles(), rbacApi.getPermissions()]);
      setRoles(r);
      setPermissions(p);
      try {
        const d = await rbacApi.getDriver();
        setDriver(d);
      } catch { /* ignore driver errors */ }
      // Load admins from v1 endpoint for role assignment ({ type, admins })
      try {
        const v1Res = await apiV1.get<{ type: string; admins?: V1Admin[] }>('/admin/all-admins');
        setAdmins(v1Res.data.admins ?? []);
      } catch { /* non-critical: admin list just won't show */ }
    } catch {
      setError(t('rollen.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  // ── Permission groups for display ────────────────────────────────────────

  const permissionGroups = React.useMemo(() => {
    const groups: Record<string, PermissionData[]> = {};
    for (const p of permissions) {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    }
    return groups;
  }, [permissions]);

  const [matrixDrawerOpen, setMatrixDrawerOpen] = useState(false);

  // ── Open matrix editor for a role ────────────────────────────────────────

  const openMatrix = (role: RoleData) => {
    setEditingRole(role);
    setMatrixPerms(new Set(role.permissions));
    setMatrixError(null);
    setMatrixDrawerOpen(true);
  };

  const closeMatrix = () => {
    setMatrixDrawerOpen(false);
    setEditingRole(null);
    setMatrixPerms(new Set());
    setMatrixError(null);
  };

  const togglePerm = (key: string) => {
    if (!canWrite) return;
    setMatrixPerms(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveMatrix = async () => {
    if (!editingRole) return;
    setMatrixSaving(true);
    setMatrixError(null);
    try {
      const updated = await rbacApi.syncPermissions(editingRole.id, Array.from(matrixPerms));
      setRoles(prev => prev.map(r => r.id === updated.id ? updated : r));
      setEditingRole(updated);
      setMatrixPerms(new Set(updated.permissions));
      refreshPermissions();
    } catch {
      setMatrixError(t('rollen.error.save'));
    } finally {
      setMatrixSaving(false);
    }
  };

  // ── Role drawer ──────────────────────────────────────────────────────────

  const openRoleDrawer = (role?: RoleData) => {
    setRoleDrawerData(role ? { id: role.id, name: role.name } : { name: '' });
    setRoleError(null);
    setRoleDrawerOpen(true);
  };

  const saveRole = async () => {
    if (!roleDrawerData.name.trim()) return;
    setRoleSaving(true);
    setRoleError(null);
    try {
      if (roleDrawerData.id) {
        const updated = await rbacApi.updateRole(roleDrawerData.id, roleDrawerData.name.trim());
        setRoles(prev => prev.map(r => r.id === updated.id ? updated : r));
      } else {
        const created = await rbacApi.createRole(roleDrawerData.name.trim());
        setRoles(prev => [...prev, created]);
      }
      setRoleDrawerOpen(false);
    } catch {
      setRoleError(t('rollen.error.save'));
    } finally {
      setRoleSaving(false);
    }
  };

  const deleteRole = async (id: number) => {
    if (!window.confirm(t('rollen.confirm.delete'))) return;
    try {
      await rbacApi.deleteRole(id);
      setRoles(prev => prev.filter(r => r.id !== id));
      if (editingRole?.id === id) closeMatrix();
    } catch {
      setError(t('rollen.error.delete'));
    }
  };

  // ── Admin assignment ─────────────────────────────────────────────────────

  const openAssign = (admin: V1Admin) => {
    setAssignAdmin(admin);
    setAssignRoleId(admin.role ?? null);
    setAssignError(null);
    setAssignDrawerOpen(true);
  };

  const saveAssign = async () => {
    if (!assignAdmin) return;
    setAssignSaving(true);
    setAssignError(null);
    try {
      await rbacApi.assignRole(assignAdmin.id, assignRoleId);
      setAdmins(prev => prev.map(a => a.id === assignAdmin.id ? { ...a, role: assignRoleId } : a));
      setAssignDrawerOpen(false);
    } catch {
      setAssignError(t('rollen.error.assign'));
    } finally {
      setAssignSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: 32, color: tc.muted, fontFamily: 'Outfit, sans-serif', fontSize: 14 }}>
        {t('common.loading_short')}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, color: '#b42318', fontFamily: 'Outfit, sans-serif', fontSize: 14 }}>
        {error}
      </div>
    );
  }

  // If no matrix is open: show roles list + (optionally) admin assignment
  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, fontFamily: 'Outfit, sans-serif' }}>
      {/* Driver badge */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <SectionTitle
          title={t('rollen.title')}
          subtitle={t('rollen.subtitle')}
          dark={dark}
        />
        <Badge tone={driver === 'spatie' ? 'success' : 'info'} dark={dark}>
          RBAC: {driver}
        </Badge>
      </div>


      {/* ── Roles table ── */}
      <Card dark={dark} style={{ marginBottom: 28 }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: tc.heading }}>
            {t('rollen.roles.title')} <Badge tone="neutral" dark={dark}>{roles.length}</Badge>
          </div>
          <Can permission="admins.write">
            <Button size="sm" dark={dark} onClick={() => openRoleDrawer()}>
              + {t('rollen.roles.new')}
            </Button>
          </Can>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                {['ID', t('rollen.col.name'), t('rollen.col.permissions'), ''].map(h => (
                  <th key={h} style={{
                    padding: '8px 24px', textAlign: 'left',
                    fontSize: 11, fontWeight: 600, color: tc.muted,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr
                  key={role.id}
                  style={{
                    borderBottom: `1px solid ${tc.border}`,
                    background: editingRole?.id === role.id ? (dark ? '#1a0d14' : '#fef7fb') : 'transparent',
                  }}
                >
                  <td style={{ padding: '12px 24px', fontSize: 12, color: tc.muted }}>{role.id}</td>
                  <td style={{ padding: '12px 24px', fontWeight: 500, color: tc.heading }}>{role.name}</td>
                  <td style={{ padding: '12px 24px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {role.permissions.slice(0, 5).map(k => (
                        <Badge key={k} tone="neutral" dark={dark}>{k}</Badge>
                      ))}
                      {role.permissions.length > 5 && (
                        <Badge tone="brand" dark={dark}>+{role.permissions.length - 5}</Badge>
                      )}
                      {role.permissions.length === 0 && (
                        <span style={{ fontSize: 12, color: tc.muted }}>{t('rollen.noPermissions')}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 24px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        dark={dark}
                        onClick={() => openMatrix(role)}
                      >
                        <Icon.Shield c={BRAND} />
                      </Button>
                      <Can permission="admins.write">
                        <Button
                          variant="ghost"
                          size="sm"
                          dark={dark}
                          onClick={() => openRoleDrawer(role)}
                        >
                          <Icon.Edit c={tc.muted} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          dark={dark}
                          onClick={() => deleteRole(role.id)}
                        >
                          <Icon.Trash c="#b42318" />
                        </Button>
                      </Can>
                    </div>
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '32px 24px', textAlign: 'center', color: tc.muted, fontSize: 13 }}>
                    {t('rollen.noRoles')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Admin → Role assignment table ── */}
      {admins.length > 0 && (
        <Card dark={dark}>
          <div style={{ padding: '20px 24px 0', fontWeight: 700, fontSize: 15, color: tc.heading }}>
            {t('rollen.admins.title')}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                  {[t('rollen.col.admin'), t('rollen.col.email'), t('rollen.col.role'), ''].map(h => (
                    <th key={h} style={{
                      padding: '8px 24px', textAlign: 'left',
                      fontSize: 11, fontWeight: 600, color: tc.muted,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => {
                  const adminRole = roles.find(r => r.id === admin.role);
                  return (
                    <tr key={admin.id} style={{ borderBottom: `1px solid ${tc.border}` }}>
                      <td style={{ padding: '12px 24px', fontWeight: 500, color: tc.heading }}>
                        {[admin.vorname, admin.nachname].filter(Boolean).join(' ') || `Admin #${admin.id}`}
                      </td>
                      <td style={{ padding: '12px 24px', fontSize: 12, color: tc.muted }}>{admin.email ?? '—'}</td>
                      <td style={{ padding: '12px 24px' }}>
                        {adminRole
                          ? <Badge tone="brand" dark={dark}>{adminRole.name}</Badge>
                          : <span style={{ fontSize: 12, color: tc.muted }}>{t('rollen.noRole')}</span>
                        }
                      </td>
                      <td style={{ padding: '12px 24px' }}>
                        <Can permission="admins.write">
                          <Button
                            variant="ghost"
                            size="sm"
                            dark={dark}
                            onClick={() => openAssign(admin)}
                          >
                            {t('rollen.assign.title')}
                          </Button>
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Drawer: permission matrix ── */}
      <Drawer open={matrixDrawerOpen} onClose={closeMatrix} dark={dark} width={600}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <div style={{ padding: '24px 24px 0', flexShrink: 0 }}>
            <SectionTitle
              title={editingRole ? t('rollen.matrix.title', { name: editingRole.name }) : ''}
              subtitle={t('rollen.matrix.subtitle')}
              dark={dark}
            />
            {matrixError && (
              <div style={{ color: '#b42318', fontSize: 12, marginTop: 12 }}>{matrixError}</div>
            )}
          </div>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {Object.entries(permissionGroups).map(([group, perms]) => (
              <div key={group} style={{ marginBottom: 20 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: tc.placeholder,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: 8,
                }}>
                  {t(`rollen.group.${group}`, { defaultValue: group })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {perms.map(p => (
                    <label
                      key={p.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${matrixPerms.has(p.key) ? BRAND : tc.border}`,
                        background: matrixPerms.has(p.key) ? (dark ? '#2a1222' : '#fce7f4') : tc.cardMuted,
                        cursor: canWrite ? 'pointer' : 'not-allowed',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Toggle
                        checked={matrixPerms.has(p.key)}
                        onChange={() => togglePerm(p.key)}
                      />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: tc.text }}>{p.label}</div>
                        <div style={{ fontSize: 10, color: tc.muted, fontFamily: 'monospace' }}>{p.key}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${tc.border}`,
            flexShrink: 0,
            display: 'flex', gap: 8,
          }}>
            {canWrite && (
              <Button onClick={saveMatrix} disabled={matrixSaving} dark={dark}>
                {matrixSaving ? '…' : t('common.action.save')}
              </Button>
            )}
            <Button variant="ghost" dark={dark} onClick={closeMatrix}>
              {t('common.action.close')}
            </Button>
          </div>
        </div>
      </Drawer>

      {/* ── Drawer: create/rename role ── */}
      <Drawer open={roleDrawerOpen} onClose={() => setRoleDrawerOpen(false)} dark={dark} width={420}>
        <div style={{ padding: 24 }}>
          <SectionTitle
            title={roleDrawerData.id ? t('rollen.drawer.edit') : t('rollen.drawer.create')}
            subtitle={t('rollen.drawer.subtitle')}
            dark={dark}
          />
          <div style={{ marginTop: 20 }}>
            <Field label={t('rollen.col.name')}>
              <Input
                dark={dark}
                value={roleDrawerData.name}
                onChange={e => setRoleDrawerData(p => ({ ...p, name: e.target.value }))}
                placeholder={t('rollen.drawer.namePlaceholder')}
              />
            </Field>
          </div>
          {roleError && (
            <div style={{ color: '#b42318', fontSize: 12, marginTop: 8 }}>{roleError}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <Button onClick={saveRole} disabled={roleSaving || !roleDrawerData.name.trim()} dark={dark}>
              {roleSaving ? '…' : t('common.action.save')}
            </Button>
            <Button variant="ghost" dark={dark} onClick={() => setRoleDrawerOpen(false)}>
              {t('common.action.cancel')}
            </Button>
          </div>
        </div>
      </Drawer>

      {/* ── Drawer: assign role to admin ── */}
      <Drawer open={assignDrawerOpen} onClose={() => setAssignDrawerOpen(false)} dark={dark} width={420}>
        <div style={{ padding: 24 }}>
          <SectionTitle
            title={t('rollen.assign.title')}
            subtitle={assignAdmin
              ? [assignAdmin.vorname, assignAdmin.nachname].filter(Boolean).join(' ') || `Admin #${assignAdmin.id}`
              : ''}
            dark={dark}
          />
          <div style={{ marginTop: 20 }}>
            <Field label={t('rollen.col.role')}>
              <select
                value={assignRoleId ?? ''}
                onChange={e => setAssignRoleId(e.target.value ? Number(e.target.value) : null)}
                style={{
                  width: '100%', padding: '10px 14px',
                  borderRadius: 8, border: `1px solid ${tc.border}`,
                  background: tc.card, color: tc.text,
                  fontSize: 14, fontFamily: 'Outfit, sans-serif',
                }}
              >
                <option value="">{t('rollen.noRole')}</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </Field>
          </div>
          {assignError && (
            <div style={{ color: '#b42318', fontSize: 12, marginTop: 8 }}>{assignError}</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <Button onClick={saveAssign} disabled={assignSaving} dark={dark}>
              {assignSaving ? '…' : t('common.action.save')}
            </Button>
            <Button variant="ghost" dark={dark} onClick={() => setAssignDrawerOpen(false)}>
              {t('common.action.cancel')}
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
